import {
  Accessor,
  createComputed,
  createMemo,
  createRoot,
  untrack,
} from "solid-js";

import UBoolean from "@repo/utils/UBoolean";

namespace UAccessor {
  export function waitUntil<T>(
    signal: Accessor<T>
  ): Promise<Exclude<T, UBoolean.Falsy>>;
  export function waitUntil<T, R extends T = T>(
    signal: Accessor<T>,
    predicate: (value: T) => value is R
  ): Promise<R>;
  export function waitUntil(
    signal: Accessor<any>,
    predicate: (value: any) => boolean = UBoolean.isTruthy
  ) {
    return new Promise((resolve) => {
      const value = signal();

      if (predicate(value)) resolve(value);

      createRoot((dispose) =>
        createComputed(() => {
          const value = signal();

          if (predicate(value)) {
            resolve(value);
            dispose();
          }
        })
      );
    });
  }

  export function split<T>(accessor: Accessor<T>) {
    return new Proxy(accessor, {
      get(target: any, prop) {
        target = createMemo(target);

        if (prop === Symbol.iterator)
          return function* () {
            const value = untrack(target);

            if (
              !(value && typeof value === "object" && Symbol.iterator in value)
            )
              throw new Error(`${value} is not iterable.`);

            // Array specific iterator
            if (Array.isArray(value))
              for (let i = 0; i < value.length; ++i)
                yield createMemo(() => target()[i]);
            // Generic iterator
            else {
              let current = 0;

              for (const // eslint-disable-next-line no-unused-vars
                _ of value as any) {
                const i = ++current;
                yield createMemo(() => {
                  let j = 0;
                  for (const item of target()) {
                    if (i === j) return item;
                    ++j;
                  }
                });
              }
            }
          };

        const value: Record<any, any> = untrack(target);
        return prop in value ? createMemo(() => target()[prop]) : undefined;
      },
    }) as any as { [Key in keyof T]: () => T[Key] };
  }
}

export default UAccessor;
